import React, { useState, useEffect } from 'react';
import api from './AxiosConfig';
import {
    Container,
    List,
    ListItem,
    ListItemText,
    IconButton,
    TextField,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem as MuiMenuItem,
    FormControl,
    InputLabel,
    Paper,
    Grid,
    Tabs,
    Tab,
} from '@mui/material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import Loading from "../Loading";

function SortableItem({ id, children }) {
    const { attributes, setNodeRef, transform, transition, listeners } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            {children(listeners)}
        </div>
    );
}

const ManageMenu = ({ token, baseUrl, onBack }) => {
    const [activeTab, setActiveTab] = useState('categories');
    const [categories, setCategories] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [editCategoryId, setEditCategoryId] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');
    const [editCategoryDialogOpen, setEditCategoryDialogOpen] = useState(false);
    const [menuItemDialogOpen, setMenuItemDialogOpen] = useState(false);
    const [menuItemForm, setMenuItemForm] = useState({
        id: null,
        name: '',
        description: '',
        price: '',
        categoryId: null,
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(''); // 'category' lub 'item'
    const [deleteId, setDeleteId] = useState(null);
    const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchCategories();
        fetchMenuItems();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await api.get(`${baseUrl}/api/restaurant/getCategories`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const sorted = response.data.sort((a, b) => a.priority - b.priority);
            setCategories(sorted);
        } catch (error) {
            alert('Błąd przy pobieraniu kategorii');
        } finally {
            setLoading(false)
        }
    };

    const fetchMenuItems = async () => {
        setLoading(true);
        try {
            const response = await api.get(`${baseUrl}/api/restaurant/getMenuItems`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMenuItems(response.data);
        } catch (error) {
            alert('Błąd przy pobieraniu pozycji menu');
        } finally {
            setLoading(false)
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = categories.findIndex((cat) => cat.id === active.id);
            const newIndex = categories.findIndex((cat) => cat.id === over.id);
            const newCategories = arrayMove(categories, oldIndex, newIndex);
            const updatedCategories = newCategories.map((cat, index) => ({
                ...cat,
                priority: index + 1,
            }));
            setCategories(updatedCategories);
            setLoading(true);
            try {
                await api.post(
                    `${baseUrl}/api/restaurant/updateCategoryPriority`,
                    updatedCategories.map((cat) => ({ id: cat.id, priority: cat.priority })),
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                fetchCategories();
            } catch (error) {
                alert('Błąd przy aktualizacji kategorii');
            } finally {
                setLoading(false)
            }
        }
    };

    const handleCategoryEdit = (category) => {
        setEditCategoryId(category.id);
        setEditCategoryName(category.name);
        setEditCategoryDialogOpen(true);
    };

    const handleCategoryNameChange = (e) => {
        setEditCategoryName(e.target.value);
    };

    const handleCategoryEditSubmitDialog = async () => {
        setLoading(true);
        try {
            await api.post(
                `${baseUrl}/api/restaurant/updateCategoryName`,
                {
                    id: editCategoryId,
                    name: editCategoryName,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setEditCategoryDialogOpen(false);
            setEditCategoryId(null);
            setEditCategoryName('');
            fetchCategories();
        } catch (error) {
            alert('Błąd przy aktualizacji nazwy kategorii');
        } finally {
            setLoading(false)
        }
    };

    const handleDelete = (type, id) => {
        setDeleteType(type);
        setDeleteId(id);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        setLoading(true);
        try {
            if (deleteType === 'category') {
                await api.post(
                    `${baseUrl}/api/restaurant/removeCategory/${deleteId}`,
                    {},
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                fetchCategories();
                fetchMenuItems();
            } else if (deleteType === 'item') {
                await api.post(
                    `${baseUrl}/api/restaurant/removeItem/${deleteId}`,
                    {},
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                fetchMenuItems();
            }
        } catch (error) {
            alert('Błąd przy usuwaniu elementu');
        } finally {
            setLoading(false)
        }
        setDeleteDialogOpen(false);
        setDeleteId(null);
        setDeleteType('');
    };

    const handleEditMenuItem = (item) => {
        // Przy edycji można zachować istniejący adres URL obrazu w menuItemForm, jeśli chcesz go pokazać,
        // ale nowo wybrany plik nadpisze poprzedni.
        setMenuItemForm({ ...item, price: item.price.toString() });
        setSelectedFile(null);
        setMenuItemDialogOpen(true);
    };

    const handleMenuItemFormChange = (e) => {
        const { name, value } = e.target;
        setMenuItemForm({ ...menuItemForm, [name]: value });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleMenuItemFormSubmit = async () => {
        // Validate "Nazwa" field
        if (!menuItemForm.name.trim()) {
            alert('Nazwa jest wymagana');
            return;
        }
        // Validate "Cena" field
        if (!menuItemForm.price.trim()) {
            alert('Cena jest obowiązkowa');
            return;
        }
        const priceRegex = /^\d+(\.\d{1,2})?$/;
        if (!priceRegex.test(menuItemForm.price)) {
            alert('Cena musi być liczbą i może zawierać maksymalnie dwie cyfry po kropce');
            return;
        }
        // Validate "Kategoria" field (it is required)
        if (!menuItemForm.categoryId) {
            alert('Kategoria jest obowiązkowa');
            return;
        }

        const formData = new FormData();
        // Dodaj plik tylko jeśli został wybrany
        if (selectedFile) {
            formData.append("file", selectedFile);
        }
        if (menuItemForm.id) {
            formData.append("id", menuItemForm.id);
        }
        formData.append("name", menuItemForm.name);
        formData.append("description", menuItemForm.description);
        formData.append("price", menuItemForm.price);
        formData.append("categoryId", menuItemForm.categoryId);
        setLoading(true);
        try {
            if (menuItemForm.id) {
                await api.post(`${baseUrl}/api/restaurant/updateItem`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                });
            } else {
                await api.post(`${baseUrl}/api/restaurant/addItem`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                });
            }
            fetchMenuItems();
            setMenuItemDialogOpen(false);
            setSelectedFile(null);
        } catch (error) {
            alert('Błąd przy zapisie pozycji menu');
        } finally {
            setLoading(false)
        }
    };

    const handleAddCategory = async () => {
        const newPriority = categories.length + 1;
        setLoading(true);
        try {
            await api.post(
                `${baseUrl}/api/restaurant/addCategory`,
                {
                    name: newCategoryName,
                    priority: newPriority,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setNewCategoryName('');
            setNewCategoryDialogOpen(false);
            fetchCategories();
        } catch (error) {
            alert('Błąd przy dodawaniu kategorii');
        } finally {
            setLoading(false)
        }
    };

    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            <Loading open={loading} />
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4">Zarządzaj Menu</Typography>
            </Box>

            <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                textColor="primary"
                indicatorColor="primary"
                sx={{ mb: 3 }}
            >
                <Tab label="Kategorie" value="categories" />
                <Tab label="Pozycje Menu" value="menuItems" />
            </Tabs>

            {activeTab === 'categories' && (
                <Box>
                    <Box mb={2}>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewCategoryDialogOpen(true)}>
                            Dodaj kategorię
                        </Button>
                    </Box>

                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={categories.map((cat) => cat.id)} strategy={verticalListSortingStrategy}>
                            <List>
                                {categories.map((category) => (
                                    <SortableItem key={category.id} id={category.id}>
                                        {(dragHandleProps) => (
                                            <Paper
                                                sx={{
                                                    p: 2,
                                                    mb: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    border: '1px solid #ccc',
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <Box {...dragHandleProps} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', flex: 1 }}>
                                                    <ListItemText primary={`${category.name} (Priorytet: ${category.priority})`} />
                                                </Box>
                                                <Box>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCategoryEdit(category);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete('category', category.id);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Paper>
                                        )}
                                    </SortableItem>
                                ))}
                            </List>
                        </SortableContext>
                    </DndContext>
                </Box>
            )}

            {activeTab === 'menuItems' && (
                <Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{ mb: 2 }}
                        onClick={() => {
                            setMenuItemForm({ id: null, name: '', description: '', price: '', categoryId: null });
                            setSelectedFile(null);
                            setMenuItemDialogOpen(true);
                        }}
                    >
                        Dodaj pozycję menu
                    </Button>
                    {categories.map((category) => (
                        <Accordion key={category.id} defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>{category.name}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {menuItems
                                    .filter((item) => item.categoryId === category.id)
                                    .map((item) => (
                                        <Box key={item.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Box display="flex" alignItems="center">
                                                {item.image && (
                                                    <img
                                                        src={item.image}
                                                        alt={item.name}
                                                        style={{
                                                            width: 50,
                                                            height: 50,
                                                            objectFit: 'cover',
                                                            marginRight: 8,
                                                            border: '1px solid #ccc',
                                                            borderRadius: 4,
                                                        }}
                                                    />
                                                )}
                                                <Box>
                                                    <Typography variant="subtitle1">{item.name}</Typography>
                                                    <Typography variant="body2">{item.description}</Typography>
                                                    <Typography variant="body2">Cena: {item.price}</Typography>
                                                </Box>
                                            </Box>
                                            <Box>
                                                <IconButton onClick={() => handleEditMenuItem(item)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton onClick={() => handleDelete('item', item.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    {menuItems.filter((item) => !item.categoryId).length > 0 && (
                        <>
                            <Box mt={4} mb={2}>
                                <Typography variant="h5">Pozycje Bez Kategorii</Typography>
                            </Box>
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Bez kategorii</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {menuItems
                                        .filter((item) => !item.categoryId)
                                        .map((item) => (
                                            <Box key={item.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                                <Box display="flex" alignItems="center">
                                                    {item.image && (
                                                        <img
                                                            src={item.image}
                                                            alt={item.name}
                                                            style={{
                                                                width: 50,
                                                                height: 50,
                                                                objectFit: 'cover',
                                                                marginRight: 8,
                                                                border: '1px solid #ccc',
                                                                borderRadius: 4,
                                                            }}
                                                        />
                                                    )}
                                                    <Box>
                                                        <Typography variant="subtitle1">{item.name}</Typography>
                                                        <Typography variant="body2">{item.description}</Typography>
                                                        <Typography variant="body2">Cena: {item.price}</Typography>
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <IconButton onClick={() => handleEditMenuItem(item)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDelete('item', item.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        ))}
                                </AccordionDetails>
                            </Accordion>
                        </>
                    )}
                </Box>
            )}

            <Dialog open={menuItemDialogOpen} onClose={() => setMenuItemDialogOpen(false)}>
                <DialogTitle>{menuItemForm.id ? 'Edycja pozycji menu' : 'Dodaj pozycję menu'}</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Nazwa"
                        name="name"
                        required
                        fullWidth
                        value={menuItemForm.name}
                        onChange={handleMenuItemFormChange}
                    />
                    <TextField
                        margin="dense"
                        label="Opis"
                        name="description"
                        fullWidth
                        value={menuItemForm.description}
                        onChange={handleMenuItemFormChange}
                    />
                    <TextField
                        margin="dense"
                        label="Cena"
                        name="price"
                        required
                        fullWidth
                        value={menuItemForm.price}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*\.?\d{0,2}$/.test(value)) {
                                handleMenuItemFormChange(e);
                            }
                        }}
                        type="number"
                        InputProps={{endAdornment: 'zł'}}
                    />
                    <Box mt={2}>
                        <Button variant="outlined" component="label">
                            Wybierz obraz
                            <input type="file" hidden onChange={handleFileChange} />
                        </Button>
                        {selectedFile ? (
                            <Box mt={2} sx={{ textAlign: 'center' }}>
                                <img
                                    src={URL.createObjectURL(selectedFile)}
                                    alt="Wybrany obraz"
                                    style={{ maxWidth: '100%', maxHeight: '200px', marginTop: '10px' }}
                                />
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Wybrany plik: {selectedFile.name}
                                </Typography>
                            </Box>
                        ) : (
                            menuItemForm.image && (
                                <Box mt={2} sx={{ textAlign: 'center' }}>
                                    <img
                                        src={menuItemForm.image}
                                        alt="Aktualny obraz"
                                        style={{ maxWidth: '100%', maxHeight: '200px', marginTop: '10px' }}
                                    />
                                </Box>
                            )
                        )}
                    </Box>
                    <FormControl fullWidth sx={{ mt: 2 }} required>
                        <InputLabel>Kategoria</InputLabel>
                        <Select
                            label="Kategoria"
                            name="categoryId"
                            value={menuItemForm.categoryId || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                setMenuItemForm({ ...menuItemForm, categoryId: value === '' ? null : value });
                            }}
                        >
                            <MuiMenuItem value="">Bez kategorii</MuiMenuItem>
                            {categories.map((cat) => (
                                <MuiMenuItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                </MuiMenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMenuItemDialogOpen(false)}>Anuluj</Button>
                    <Button onClick={handleMenuItemFormSubmit}>Zapisz</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={newCategoryDialogOpen} onClose={() => setNewCategoryDialogOpen(false)}>
                <DialogTitle>Dodaj kategorię</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nazwa kategorii"
                        fullWidth
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewCategoryDialogOpen(false)}>Anuluj</Button>
                    <Button onClick={handleAddCategory}>Dodaj</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Potwierdzenie usunięcia</DialogTitle>
                <DialogContent>
                    <Typography>Czy na pewno chcesz usunąć wybrany element?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
                    <Button onClick={confirmDelete} color="error">
                        Usuń
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editCategoryDialogOpen} onClose={() => setEditCategoryDialogOpen(false)}>
                <DialogTitle>Edycja nazwy kategorii</DialogTitle>
                <DialogContent>
                    <TextField margin="dense" label="Nazwa" fullWidth value={editCategoryName} onChange={handleCategoryNameChange} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditCategoryDialogOpen(false)}>Anuluj</Button>
                    <Button onClick={handleCategoryEditSubmitDialog}>Zapisz</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ManageMenu;